import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useReportTagUsage } from '@/hooks/use-report-tag-usage';
import { fuzzyFilter } from '@/lib/fuzzy';
import { cn } from '@/lib/utils';
import { Search, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearch } from 'wouter';
// The resting list is deliberately short — the point is "what does my caseload
// look like", not a full vocabulary browser (that's Settings -> Standard
// Reporting -> Clinical tags). Search appears once there is more than fits.
const TOP_TAGS = 5;
const SEARCH_RESULTS_MAX = 8;
/**
 * "Clinical tags" sidebar section (below Folders): the top tags actually
 * asserted on visible studies, each with its study count. Clicking one merges
 * `tag=` into the worklist URL — same navigation pattern as the sidebar time
 * filters — and clicking the active one clears it. With more tags than the
 * resting list shows, a fuzzy search box (label + aliases) appears.
 */
export function ClinicalTagsNav() {
    const { t } = useTranslation();
    const [location, setLocation] = useLocation();
    const searchString = useSearch();
    const { tags } = useReportTagUsage();
    const [query, setQuery] = useState('');
    const activeTag = useMemo(() => new URLSearchParams(searchString).get('tag')?.toLowerCase() ?? null, [searchString]);
    const searching = query.trim().length > 0;
    const visibleTags = useMemo(() => {
        if (!searching)
            return tags.slice(0, TOP_TAGS);
        return fuzzyFilter(tags, query, (tag) => [tag.label, ...tag.aliases]).slice(0, SEARCH_RESULTS_MAX);
    }, [tags, query, searching]);
    // Nothing tagged yet: render nothing rather than an empty header — the
    // feature introduces itself the first time a report carries a tag.
    if (tags.length === 0)
        return null;
    const handleTagClick = (label) => {
        const isActive = activeTag === label.toLowerCase();
        // Merge into the worklist's existing URL state (mirrors the sidebar time
        // filters): coming from another page starts clean, on the worklist the
        // user's other filters survive and paging restarts.
        const onWorklist = location.startsWith('/study');
        const params = new URLSearchParams(onWorklist ? searchString : '');
        params.delete('page');
        if (isActive) {
            params.delete('tag');
        }
        else {
            params.set('tag', label);
        }
        const qs = params.toString();
        setLocation(qs ? `/study/list?${qs}` : '/study/list', { replace: onWorklist });
    };
    return (_jsxs("div", { "data-testid": "clinical-tags-nav", children: [_jsx("div", { className: "mt-4 flex items-center justify-between px-4 pb-1", children: _jsx("span", { className: "text-muted-foreground/60 text-[10px] font-semibold tracking-widest uppercase", children: t('sidebar.clinicalTags') }) }), tags.length > TOP_TAGS && (_jsxs("div", { className: "relative mb-1 px-3", children: [_jsx(Search, { className: "text-muted-foreground pointer-events-none absolute top-1/2 left-5 h-3 w-3 -translate-y-1/2" }), _jsx("input", { type: "text", value: query, onChange: (e) => setQuery(e.target.value), placeholder: t('sidebar.searchClinicalTags'), "aria-label": t('sidebar.searchClinicalTags'), className: "border-border/60 bg-background/40 focus:ring-ring h-6.5 w-full rounded-md border py-1 pr-2 pl-7 text-xs focus:ring-1 focus:outline-none" })] })), _jsxs("div", { className: "space-y-0.5 px-2", children: [visibleTags.map((tag) => {
                        const isActive = activeTag === tag.label.toLowerCase();
                        return (_jsxs("button", { onClick: () => handleTagClick(tag.label), title: isActive ? t('sidebar.clearTagFilter') : tag.label, className: cn('relative flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-[13px] transition-colors', isActive
                                ? 'text-foreground bg-[#3FB4A8]/[0.08] before:pointer-events-none before:absolute before:top-1/2 before:left-2 before:h-[15px] before:w-[2px] before:-translate-y-1/2 before:rounded-full before:bg-[#3FB4A8] before:opacity-80'
                                : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'), children: [_jsxs("span", { className: "flex min-w-0 items-center gap-2", children: [_jsx(Tag, { className: "h-3 w-3 shrink-0 opacity-60", "aria-hidden": true }), _jsx("span", { className: "truncate", children: tag.label })] }), _jsx("span", { className: "text-muted-foreground/80 min-w-[1.5rem] shrink-0 text-right font-mono text-xs tabular-nums", children: tag.studyCount })] }, tag.id));
                    }), searching && visibleTags.length === 0 && (_jsx("p", { className: "text-muted-foreground/60 px-3 py-1.5 text-xs", children: t('sidebar.noTagsMatch') }))] })] }));
}
